/**
 * @Author: Nemo
 * @Description: 表单字段配置入口文件
 * @Date: 2024-11-30
 * @FilePath: /rhcn-checkout-web/src/common/config/field-config/index.js
 */

// 导入字段基础配置
import { paymentFields, billingFields } from './field';
// 导入字段排序相关方法
import { getRuleList, formatRules, generateFieldsWithRules, getIdentityNumberLabel } from './rules';
import { sortFieldsByType, paymentFieldsOrder, billingFieldsOrder } from './sort';

/**
 * 创建一个行字段配置 - 用于将多个字段并排显示
 * @param {Object} options 配置选项
 * @param {string} options.name 行字段名称
 * @param {string} options.label 行字段标签
 * @param {Array} options.fields 子字段配置数组
 * @param {string} options.display 是否显示 'Y'/'N'
 * @param {string} options.require 是否必填 'Y'/'N'
 * @returns {Object} 行字段配置对象
 */
export function createRowFields(options = {}) {
  const {
    name,
    label,
    fields = [],
    display = 'Y',
    require = 'N',
  } = options;

  return {
    name,
    label,
    display,
    require,
    isRowFields: true,
    rowFields: fields,
  };
}

/**
 * 获取完整的收银台字段池
 * @returns {Object} 包含支付和账单字段的基础字段池
 */
export function getCompleteFieldPool() {
  // 获取基础字段配置
  let paymentPool = [...paymentFields];
  let billingPool = [...billingFields];

  // 生成基础验证规则（使用默认国家代码）
  const paymentRules = getRuleList(paymentPool, 'US');
  const billingRules = getRuleList(billingPool, 'US');

  // 将验证规则集成到字段配置中
  paymentPool = paymentPool.map(field => {
    if (paymentRules[field.name]) {
      return { ...field, validationRules: paymentRules[field.name] };
    }
    return field;
  });

  billingPool = billingPool.map(field => {
    if (billingRules[field.name]) {
      return { ...field, validationRules: billingRules[field.name] };
    }
    return field;
  });

  // 排序字段
  paymentPool = sortFieldsByType('payment', paymentPool);
  billingPool = sortFieldsByType('billing', billingPool);

  return {
    payment: paymentPool,
    billing: billingPool,
  };
}

/**
 * 获取最终字段配置 - 将API返回的字段配置与基础字段池结合，生成UI组件需要的格式
 * @param {Object} options 配置选项
 * @param {Array} options.apiFields API返回的字段配置数组
 * @param {string} options.fieldType 字段类型('payment'或'billing')
 * @param {string} options.country 国家代码
 * @returns {Array} 处理后的字段配置数组
 */
export function getFieldConfig(options = {}) {
  const {
    apiFields = [],
    fieldType = 'payment',
    country = 'US',
  } = options;

  // 如果没有API字段，直接返回空数组
  if (!apiFields.length) {
    return [];
  }

  // 获取基础字段池
  const fieldPool = getCompleteFieldPool();

  // 根据字段类型选择对应的字段池
  const baseFields = fieldType === 'payment' ? fieldPool.payment : fieldPool.billing;

  // 创建基础字段映射，用于快速查找
  const baseFieldMap = {};
  baseFields.forEach(field => {
    baseFieldMap[field.name] = field;
  });

  // 收集所有行字段内部的子字段名称，用于后续过滤
  const subFieldNames = new Set();
  baseFields.forEach(field => {
    if (field.isRowFields && field.rowFields && Array.isArray(field.rowFields)) {
      field.rowFields.forEach(subfield => {
        subFieldNames.add(subfield.name);
      });
    }
  });

  // 生成特定国家的验证规则
  const rulesMap = getRuleList(apiFields, country);

  // 处理API返回的字段
  const mergedFields = apiFields
    .filter(field => field.display === 'Y') // 只包含显示的字段
    .map(apiField => {
      // 获取基础字段配置
      const baseField = baseFieldMap[apiField.name] || {};

      // 特殊处理identityNumber字段标签
      if (apiField.name === 'identityNumber' && country) {
        const customLabel = getIdentityNumberLabel(country);
        if (customLabel && customLabel !== 'document') {
          apiField.label = customLabel;
        }
      }

      // 合并配置
      const mergedField = {
        // 基础UI配置
        ...baseField,
        ...apiField,
        // 处理验证规则
        validationRules: rulesMap[apiField.name] || baseField.validationRules || [],
      };

      // 如果是行字段并且基础配置中有行字段定义，保留行字段配置
      if (baseField.isRowFields && baseField.rowFields) {
        mergedField.isRowFields = true;
        mergedField.rowFields = baseField.rowFields.map(rowField => {
          // 查找API中对应的子字段，合并配置
          const apiRowField = apiFields.find(f => f.name === rowField.name);
          if (apiRowField) {
            return {
              ...rowField,
              ...apiRowField,
              // 子字段的验证规则
              validationRules: rulesMap[rowField.name] || rowField.validationRules || [],
            };
          }
          return rowField;
        });
      }

      return mergedField;
    })
    // 过滤掉已经在行字段组内的字段
    .filter(field => !subFieldNames.has(field.name) || field.isRowFields);

  // 应用排序并返回结果
  return sortFieldsByType(fieldType, mergedFields);
}

// 导出所有模块的功能
export {
  // 字段基础配置
  paymentFields,
  billingFields,

  // 排序相关
  sortFieldsByType,
  paymentFieldsOrder,
  billingFieldsOrder,

  // 验证规则相关
  getRuleList,
  formatRules,
  generateFieldsWithRules,
  getIdentityNumberLabel,

};
